import InfoRounded from '@mui/icons-material/InfoRounded';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

export type RadioComponentSwitchFilledOptionProps = {
  label: string;
  helperText: ReactNode;
  child: ReactNode;
  dataTest: string;
  icon?: ReactNode;
  value?: string;
  tooltip?: string | null;
  isAllowed?: boolean;
};

type RadioComponentSwitchFilledProps = {
  children?: ReactNode;
  onSwitchChange?: (value: string) => void;
  divider?: boolean;
  radioOptions: RadioComponentSwitchFilledOptionProps[];
  value?: number | string | null;
  disabled?: boolean;
  isDetailsPage?: boolean;
};

export function RadioComponentSwitchFilled({
  children,
  divider,
  radioOptions,
  value = '',
  onSwitchChange,
  disabled = false,
  isDetailsPage = false,
}: RadioComponentSwitchFilledProps) {
  const allowedOptions = radioOptions.filter(option => Boolean(option.isAllowed));

  const renderSelectedOptionChild = () => {
    if (!value) {
      return null;
    }

    const selectedOption = radioOptions.find((option, index) => (option.value ?? String(index)) === value);

    return selectedOption?.child ?? null;
  };

  return (
    <Box sx={{ width: 'fit-content' }}>
      <RadioGroup
        row
        value={value}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          if (!disabled && !isDetailsPage && onSwitchChange) {
            onSwitchChange(event.target.value);
          }
        }}
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${allowedOptions.length}, minmax(0, 272px))`,
          gap: 2,
        }}
      >
        {allowedOptions.map(({ label, helperText, dataTest, icon, value: optionValue, tooltip }, index) => {
          const radioValue = optionValue ?? String(index);
          const isSelected = value === radioValue;
          return (
            <Box
              key={radioValue}
              data-test={dataTest}
              sx={theme => ({
                border: `1px solid ${isSelected ? theme.palette.primary.light : theme.palette.divider}`,
                backgroundColor: isSelected ? theme.palette.primary.light : theme.palette.background.boxLight,
                borderRadius: 2,
                opacity: disabled && !isSelected ? 0.5 : 1,
              })}
            >
              <FormControlLabel
                value={radioValue}
                disabled={disabled}
                control={<Radio disabled={disabled} />}
                data-test="label"
                sx={{
                  alignItems: 'center',
                  margin: 0,
                  padding: 1,
                  width: '100%',
                  height: '100%',
                  '& .MuiFormControlLabel-label': { marginBottom: 'auto' },
                }}
                label={
                  <Stack spacing={0.5}>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: 'center',
                      }}
                    >
                      {icon}
                      <Typography
                        variant="body1Bold"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        {label}
                      </Typography>
                      {tooltip && (
                        <Tooltip title={tooltip} placement="right">
                          <IconButton size="small">
                            <InfoRounded />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                    <Typography
                      variant="body2"
                      data-test="helper-text"
                      sx={{
                        color: 'text.primary',
                        margin: 0,
                      }}
                    >
                      {helperText}
                    </Typography>
                  </Stack>
                }
              />
            </Box>
          );
        })}
      </RadioGroup>

      {divider && <Divider sx={{ marginBottom: 3 }} />}
      {children}
      {renderSelectedOptionChild()}
    </Box>
  );
}
